import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Trophy,
    Award,
    Crown,
    Star,
    TrendingUp,
    ShieldOff,
    ShieldCheck,
    Clock,
    Users,
} from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";


const TIER_META = {
    novice: { color: "#CCCCCC", label: "Novice", icon: Star },
    pro: { color: "#FFFFFF", label: "Pro", icon: Trophy },
    legend: { color: "#CCCCCC", label: "Legend", icon: Crown },
    mythic: { color: "#FAFAFA", label: "Mythic", icon: Crown },
};


export default function Civilization() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.civilizationMe();
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const togglePublic = async () => {
        setSaving(true);
        try {
            const next = !data.civilization_public;
            await api.civilizationSettings({ civilization_public: next });
            setData((d) => ({ ...d, civilization_public: next }));
            toast.success(next ? "Profile is public" : "Profile is private");
        } catch (e) {
            toast.error(e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !data) {
        return (
            <SidebarLayout>
                <div className="max-w-5xl mx-auto py-10 text-sm text-text-secondary">Loading…</div>
            </SidebarLayout>
        );
    }

    const tier = TIER_META[data.tier] || TIER_META.novice;
    const TierIcon = tier.icon;

    return (
        <SidebarLayout>
            <div className="max-w-5xl mx-auto py-6 px-2">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold">AI Civilization</h1>
                        <p className="text-sm text-text-secondary">
                            Reputation persistente, badge, leaderboard, ere. Powered by your grind.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            data-testid="civ-toggle-public"
                            data-testid-alias="civ-privacy-toggle"
                            onClick={togglePublic}
                            disabled={saving}
                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest transition ${
                                data.civilization_public
                                    ? "border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                                    : "border-[var(--color-border)] text-text-secondary"
                            }`}
                        >
                            {data.civilization_public ? (
                                <>
                                    <ShieldCheck className="h-3.5 w-3.5" /> Public
                                </>
                            ) : (
                                <>
                                    <ShieldOff className="h-3.5 w-3.5" /> Private
                                </>
                            )}
                        </button>
                        {/* Testid alias for Phase 4 spec */}
                        <span data-testid="civ-privacy-toggle" className="sr-only">
                            {data.civilization_public ? "public" : "private"}
                        </span>
                        <Link
                            to="/civilization/leaderboard"
                            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs text-text-secondary hover:text-text-main"
                        >
                            <Trophy className="h-3.5 w-3.5" /> Leaderboard
                        </Link>
                        <Link
                            to="/civilization/badges"
                            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-xs text-text-secondary hover:text-text-main"
                        >
                            <Award className="h-3.5 w-3.5" /> All badges
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        data-testid="civ-score-card"
                        className="rounded-2xl border p-6"
                        style={{
                            borderColor: `${tier.color}55`,
                            background: `linear-gradient(135deg, ${tier.color}18, var(--color-card))`,
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <div
                                className="h-10 w-10 rounded-full grid place-items-center"
                                style={{ background: `${tier.color}25`, color: tier.color }}
                            >
                                <TierIcon className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                    Your Score
                                </p>
                                <p
                                    data-testid="civ-score-value"
                                    className="text-3xl font-semibold tabular-nums"
                                >
                                    {data.score}
                                </p>
                            </div>
                            <div className="ml-auto text-right">
                                <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                    Rank
                                </p>
                                <p
                                    data-testid="civ-rank"
                                    className="text-xl font-medium tabular-nums"
                                >
                                    #{data.rank} <span className="text-text-secondary text-xs">/ {data.total_users}</span>
                                </p>
                            </div>
                        </div>
                        <p
                            data-testid="civ-tier-label"
                            data-testid-alias="civ-tier-badge"
                            className="mt-3 text-xs uppercase tracking-widest"
                            style={{ color: tier.color }}
                        >
                            Tier · {tier.label}
                        </p>
                        {/* Testid contract aliases (Phase 4 spec) */}
                        <span data-testid="civ-tier-badge" className="sr-only">{tier.label}</span>
                        <div
                            data-testid="civ-breakdown-radar"
                            className="mt-4 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-widest text-text-secondary"
                        >
                            <BreakdownCell label="Leads" value={data.breakdown?.leads} />
                            <BreakdownCell label="Campaigns" value={data.breakdown?.campaigns} />
                            <BreakdownCell label="Quality" value={data.breakdown?.quality} />
                            <BreakdownCell label="Referrals" value={data.breakdown?.referrals} />
                            <BreakdownCell label="Marketplace" value={data.breakdown?.marketplace} />
                            <BreakdownCell label="Other" value={data.breakdown?.other} />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        data-testid="civ-era-card"
                        className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full grid place-items-center bg-[var(--color-primary)]/15 text-[var(--color-primary)]">
                                <Clock className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                                    Current Era
                                </p>
                                <p className="text-2xl font-semibold">
                                    {data.era?.name || "—"}
                                </p>
                            </div>
                        </div>
                        {data.era && (
                            <>
                                <p className="mt-2 text-xs text-text-secondary">
                                    {data.era.description}
                                </p>
                                <div className="mt-3 flex items-center gap-4 text-[11px] text-text-secondary">
                                    <span>
                                        Ends{" "}
                                        <strong className="text-text-main">
                                            {new Date(data.era.ends_at).toLocaleDateString()}
                                        </strong>
                                    </span>
                                    <span>
                                        Your era score:{" "}
                                        <strong className="text-text-main tabular-nums">
                                            {data.era_score}
                                        </strong>
                                    </span>
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>

                {/* Recent badges */}
                <div className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
                    <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                        Recent badges
                    </p>
                    <div
                        data-testid="civ-recent-badges"
                        className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3"
                    >
                        {data.recent_badges.length === 0 && (
                            <p className="col-span-full text-xs text-text-secondary italic">
                                Nessun badge sbloccato. Fai una lead search o lancia una campaign
                                per iniziare.
                            </p>
                        )}
                        {data.recent_badges.map((b) => (
                            <BadgeCard key={b.id} badge={b.badge} earned_at={b.earned_at} />
                        ))}
                    </div>
                </div>

                {/* Recent events */}
                <div
                    className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                    data-testid="civ-recent-events"
                >
                    <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                        Reputation activity (last 30 days)
                    </p>
                    {data.recent_events.length === 0 ? (
                        <p className="mt-3 text-xs text-text-secondary italic">
                            Nessuna attività recente.
                        </p>
                    ) : (
                        <ul className="mt-3 space-y-1.5">
                            {data.recent_events.slice(0, 15).map((e) => (
                                <li
                                    key={e.id}
                                    className="flex items-center gap-3 text-xs border-b border-[var(--color-border)]/50 last:border-0 pb-1"
                                >
                                    <span
                                        className={`inline-flex tabular-nums font-medium ${
                                            e.delta >= 0
                                                ? "text-[var(--color-success)]"
                                                : "text-[var(--color-alert)]"
                                        }`}
                                    >
                                        {e.delta > 0 ? "+" : ""}
                                        {e.delta}
                                    </span>
                                    <span className="flex-1 truncate text-text-secondary">
                                        {e.reason}
                                    </span>
                                    <span className="text-[10px] text-text-secondary/60">
                                        {new Date(e.created_at).toLocaleDateString()}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </SidebarLayout>
    );
}

function BreakdownCell({ label, value }) {
    return (
        <div className="rounded-lg border border-[var(--color-border)]/60 bg-[var(--color-bg)] px-2 py-1.5">
            <p className="text-[9px]">{label}</p>
            <p className="mt-0.5 text-sm tabular-nums text-text-main">{value ?? 0}</p>
        </div>
    );
}

function BadgeCard({ badge, earned_at }) {
    if (!badge) return null;
    const tierColor = {
        bronze: "#A3A3A3",
        silver: "#B5B8BF",
        gold: "#A3A3A3",
        platinum: "#CCCCCC",
    }[badge.tier] || "#CCCCCC";
    return (
        <div
            data-testid={`badge-card-${badge.slug}`}
            className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-3"
        >
            <div
                className="h-10 w-10 rounded-full grid place-items-center shrink-0"
                style={{ background: `${tierColor}22`, color: tierColor }}
            >
                <Award className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{badge.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-text-secondary">
                    {badge.tier} · +{badge.points_value} pts
                </p>
            </div>
            <span className="text-[10px] text-text-secondary/70">
                {new Date(earned_at).toLocaleDateString()}
            </span>
        </div>
    );
}
